import { decodeJwt } from "jose";
import { createStorage } from "unstorage";
import localstorage from "unstorage/drivers/localstorage";
import memory from "unstorage/drivers/memory";
import { createCookiesAPI } from "./cookies.mjs";
import { decryptJWT, encryptJWT } from "./encryption.mjs";
import { browserOrNode } from "../utils.mjs";
const { isBrowser } = browserOrNode();
const generateId = ()=>{
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    return Array.from(bytes).map((b)=>b.toString(16).padStart(2, '0')).join('');
};
const SESSION_COOKIE_NAME = '__session';
class SessionStore {
    secret;
    sessionCookieName;
    rolling;
    absoluteDuration;
    inactivityDuration;
    cookieConfig;
    cookiesAPI;
    storage;
    constructor({ secret, rolling = true, absoluteDuration = 2592000, inactivityDuration = 1800, cookieOptions }){
        this.secret = secret;
        this.rolling = rolling;
        this.absoluteDuration = absoluteDuration;
        this.inactivityDuration = inactivityDuration;
        this.sessionCookieName = cookieOptions?.name ?? SESSION_COOKIE_NAME;
        this.cookieConfig = {
            httpOnly: false,
            sameSite: cookieOptions?.sameSite ?? 'lax',
            secure: cookieOptions?.secure ?? false,
            path: cookieOptions?.path ?? '/',
            domain: cookieOptions?.domain ?? window.location.hostname
        };
        this.cookiesAPI = createCookiesAPI(this.cookieConfig);
        this.storage = createStorage({
            driver: isBrowser ? localstorage({
                base: 'encrypted'
            }) : memory()
        });
    }
    async getEncryptedJWT() {
        const cookieValue = await this.storage.getItem(this.sessionCookieName);
        if (!cookieValue) return null;
        let sessionId = null;
        try {
            const { payload: sessionCookie } = await decryptJWT(cookieValue, this.secret);
            sessionId = sessionCookie.id;
            if (!sessionId) return null;
            return sessionCookie;
        } catch (e) {
            this.storage.removeItem(this.sessionCookieName);
            return null;
        }
    }
    async setEncryptedJWT(session, isNew = false) {
        let sessionId = null;
        const cookieValue = await this.storage.getItem(this.sessionCookieName);
        if (cookieValue) try {
            const { payload: sessionCookie } = await decryptJWT(cookieValue, this.secret);
            sessionId = sessionCookie.id;
        } catch (e) {
            this.storage.removeItem(this.sessionCookieName);
        }
        if (sessionId && isNew) {
            await this.storage.removeItem(sessionId);
            sessionId = generateId();
        }
        if (!sessionId) sessionId = generateId();
        const jwe = await encryptJWT({
            id: sessionId,
            ...session
        }, this.secret);
        await this.storage.setItem(this.sessionCookieName, jwe.toString());
    }
    getPlainText() {
        return this.cookiesAPI.get(this.sessionCookieName);
    }
    setPlainText(data, isNew = false) {
        if (isNew) this.cookiesAPI.remove(this.sessionCookieName);
        let maxAge = this.calculateMaxAge(this.epoch());
        try {
            const jwtData = decodeJwt(data);
            maxAge = jwtData ? this.calculateMaxAge(jwtData.iat || this.epoch(), jwtData.exp) : this.calculateMaxAge(this.epoch());
        } catch (e) {}
        this.cookiesAPI.set(this.sessionCookieName, data, {
            ...this.cookieConfig,
            'max-age': `${maxAge}`
        });
    }
    async delete() {
        this.cookiesAPI.remove(this.sessionCookieName);
        return await this.storage.removeItem(this.sessionCookieName);
    }
    epoch() {
        return Date.now() / 1000 | 0;
    }
    calculateMaxAge(createdAt, expiresAt) {
        if (!this.rolling) return this.absoluteDuration;
        const updatedAt = this.epoch();
        if (expiresAt) {
            const maxAge = expiresAt - this.epoch();
            return maxAge > 0 ? maxAge : 0;
        }
        {
            const expiresAt = Math.min(updatedAt + this.inactivityDuration, createdAt + this.absoluteDuration);
            const maxAge = expiresAt - this.epoch();
            return maxAge > 0 ? maxAge : 0;
        }
    }
}
export { SessionStore };
